/**
 * The preview feature module (W2-02 preview-panel; DESIGN.md §4.6, §4.8-4.9, §5.6.6; ARCHITECTURE.md §7.3-7.5).
 *
 * Provides:
 *  - panel `chart.preview` (also the Laptop layout's floating mini preview, hosted by the shell);
 *  - commands `preview.*` (Appendix B) with their key bindings (Theater `\`, test play F5 / Ctrl+Enter / Shift+F5,
 *    auto-key Alt+K, readout Shift+I; RPE preset: Tab for the readout as well);
 *  - the editor overlays (safe areas, invisible lines, ghosts, parent links, HUD outlines, labels and active glow,
 *    selection and click-to-select, lane link, sample handles, gizmo);
 *  - status items: the "Preview differs from export" banner (the only implementation of that banner) and the
 *    full-window host of Theater and test play;
 *  - when-atoms `testPlay` and `theater`;
 *  - settings rows for its `ext.preview.*` preferences and help topics;
 *  - `init`: the preview mode, auto-key and Phira player settings follow the user settings; the hitsound emission
 *    source follows the engine, the preview filters and test play; closing the project ends test play and Theater.
 */

import type { FeatureModule, SettingDef } from '../../app/registry/types';
import type { PhiraPlayerSettings } from '../../core/engine/types';
import type { Unsubscribe } from '../../core/util/types';
import type { MessageKey } from '../../i18n/types';
import { PREVIEW_COMMANDS, PREVIEW_KEYBINDINGS } from './commands';
import { registerServices } from './live';
import { createGhostsOverlay } from './overlays/ghosts';
import { createGizmoOverlay } from './overlays/gizmo';
import { createHudOutlineOverlay } from './overlays/hudOutline';
import { createInvisibleLinesOverlay } from './overlays/invisibleLines';
import { createLabelsOverlay } from './overlays/labels';
import { createLaneLinkOverlay } from './overlays/laneLink';
import { createParentLinksOverlay } from './overlays/parentLinks';
import { createSafeAreasOverlay } from './overlays/safeAreas';
import { createSampleModeOverlay } from './overlays/sampleMode';
import { cancelParentPick, createSelectionOverlay } from './overlays/selection';
import { m } from './preview.messages';
import { PreviewPanel } from './PreviewPanel';
import { PreviewDiffersBanner, PreviewStageHost } from './PreviewStageHost';
import { disposePreviewStage } from './previewLoop';
import {
  EXT_LANE_LINK,
  EXT_LEAD_IN,
  EXT_READOUT,
  EXT_SCREEN,
  EXT_TEST_MODE,
  SCREEN_SHAPES,
  type ScreenShape,
} from './prefs';
import { peekTestPlay, syncEmissionSource, testPlayOf } from './testPlayState';

function samePlayer(a: PhiraPlayerSettings, b: PhiraPlayerSettings): boolean {
  return (
    a.aspectOverride === b.aspectOverride &&
    a.noteScale === b.noteScale &&
    a.doubleHint === b.doubleHint &&
    a.particles === b.particles &&
    a.aggressiveCull === b.aggressiveCull &&
    a.strictAutoplayHitColor === b.strictAutoplayHitColor &&
    a.msaa === b.msaa &&
    a.apFcIndicator === b.apFcIndicator
  );
}

const SCREEN_KEYS: Readonly<Record<ScreenShape, MessageKey>> = {
  chart: m.screenOptionChart,
  fill: m.screenOptionFill,
  '16:9': m.screenOpt169,
  '4:3': m.screenOpt43,
  '3:2': m.screenOpt32,
  '19.5:9': m.screenOpt195,
  '21:9': m.screenOpt219,
};

const SETTINGS: readonly SettingDef[] = [
  {
    path: `ext.${EXT_SCREEN}`,
    title: m.setScreen,
    description: m.setScreenDesc,
    level: 'guided',
    kind: 'enum',
    options: SCREEN_SHAPES.map((value) => ({
      value,
      label: SCREEN_KEYS[value],
    })),
  },
  { path: `ext.${EXT_READOUT}`, title: m.setReadout, description: m.setReadoutDesc, level: 'guided', kind: 'boolean' },
  {
    path: `ext.${EXT_LANE_LINK}`,
    title: m.cmdLaneLink,
    description: m.setLaneLinkDesc,
    level: 'full',
    kind: 'boolean',
  },
  {
    path: `ext.${EXT_TEST_MODE}`,
    title: m.setTestMode,
    description: m.setTestModeDesc,
    level: 'guided',
    kind: 'enum',
    options: [
      { value: 'autoplay', label: m.modeAutoplay },
      { value: 'judged', label: m.modeJudged },
    ],
  },
  {
    path: `ext.${EXT_LEAD_IN}`,
    title: m.setLeadIn,
    description: m.setLeadInDesc,
    level: 'full',
    kind: 'number',
    min: 0,
    max: 16,
    step: 1,
  },
];

export default {
  id: 'preview',
  commands: PREVIEW_COMMANDS,
  keybindings: PREVIEW_KEYBINDINGS,
  panels: [
    {
      id: 'chart.preview',
      title: m.panelTitle,
      icon: 'MonitorPlay',
      slot: 'chart.preview',
      order: 0,
      level: 'guided',
      component: PreviewPanel,
    },
  ],
  previewOverlays: (s) => [
    createSafeAreasOverlay(s),
    createInvisibleLinesOverlay(s),
    createGhostsOverlay(s),
    createParentLinksOverlay(s),
    createHudOutlineOverlay(s),
    createLabelsOverlay(s),
    createSelectionOverlay(s),
    createLaneLinkOverlay(s),
    createSampleModeOverlay(s),
    createGizmoOverlay(s),
  ],
  statusItems: [
    { id: 'preview.differs', slot: 'banner', order: 50, component: PreviewDiffersBanner },
    { id: 'preview.stage', slot: 'banner', order: 1000, component: PreviewStageHost },
  ],
  whenAtoms: (s) => ({
    testPlay: () => peekTestPlay(s)?.active ?? false,
    theater: () => s.editor.getState().preview.theater,
  }),
  settings: SETTINGS,
  helpTopics: [
    {
      id: 'preview.overview',
      title: m.helpPreviewTitle,
      body: m.helpPreviewBody,
      level: 'guided',
      showMe: { panel: 'chart.preview' },
    },
    {
      id: 'preview.gizmo',
      title: m.helpGizmoTitle,
      body: m.helpGizmoBody,
      level: 'guided',
      showMe: { helpId: 'preview-stage' },
    },
    { id: 'preview.testPlay', title: m.helpTestPlayTitle, body: m.helpTestPlayBody, level: 'guided' },
  ],
  init(services) {
    registerServices(services);
    const offs: Unsubscribe[] = [];
    const editor = (): ReturnType<typeof services.editor.getState> => services.editor.getState();
    // Preview mode (Edit by default, remembered per user), auto-key default and the Phira player settings.
    let prev = services.settings.value;
    editor().setPreview({
      mode: prev.preview.defaultMode,
      autoKey: prev.editing.autoKeyDefault,
      player: prev.preview.player,
    });
    offs.push(
      services.settings.subscribe((v) => {
        const st = editor();
        if (v.preview.defaultMode !== prev.preview.defaultMode && st.preview.mode !== v.preview.defaultMode) {
          st.setPreview({ mode: v.preview.defaultMode });
        }
        if (v.editing.autoKeyDefault !== prev.editing.autoKeyDefault)
          st.setPreview({ autoKey: v.editing.autoKeyDefault });
        if (!samePlayer(v.preview.player, st.preview.player)) st.setPreview({ player: v.preview.player });
        prev = v;
      }),
    );
    // Hitsounds follow the engine (project swaps) and test play; the filters are read live.
    syncEmissionSource(services);
    offs.push(
      services.onChange(() => {
        if (services.store === null || services.engine === null) {
          const tp = peekTestPlay(services);
          if (tp?.active === true) tp.exit('here');
          if (editor().preview.theater) editor().setPreview({ theater: false });
          cancelParentPick(services);
        }
        syncEmissionSource(services);
      }),
    );
    return () => {
      for (const off of offs) off();
      testPlayOf(services).dispose();
      disposePreviewStage(services);
    };
  },
} satisfies FeatureModule;
